// Water chemistry: master parameter catalogue, per-body-type range resolution,
// evaluation and Langelier Saturation Index.
// Effective ranges = master default  →  type template  →  per-body override.

export const WT_PARAMS = [
  { key: "temp",              label: "Temp (\u00b0C)",       min: 0,    max: 40,   dec: 0 },
  { key: "total_chlorine",    label: "Total Chlorine",       min: 1,    max: 3,    dec: 2, unit: "ppm" },
  { key: "free_chlorine",     label: "Free Chlorine",        min: 1,    max: 3,    dec: 2, unit: "ppm" },
  { key: "combined_chlorine", label: "Combined Chlorine",    min: 0,    max: 0,    dec: 2, unit: "ppm" },
  { key: "bromine",           label: "Total Bromine",        min: 4,    max: 6,    dec: 1, unit: "ppm" },
  { key: "salt",              label: "Salt",                 min: 3750, max: 4250, dec: 0, unit: "ppm" },
  { key: "magnesium",         label: "Magnesium",            min: 80,   max: 150,  dec: 0, unit: "ppm" },
  { key: "tds",               label: "TDS",                  min: 0,    max: 2500, dec: 0, unit: "ppm" },
  { key: "ph",                label: "pH",                   min: 7.4,  max: 7.6,  dec: 1 },
  { key: "total_alkalinity",  label: "Total Alkalinity",     min: 80,   max: 125,  dec: 0, unit: "ppm" },
  { key: "cyanuric_acid",     label: "Cyanuric Acid",        min: 0,    max: 100,  dec: 0, unit: "ppm" },
  { key: "total_hardness",    label: "Total Hardness",       min: 200,  max: 400,  dec: 0, unit: "ppm" },
  { key: "calcium_hardness",  label: "Calcium Hardness",     min: 200,  max: 250,  dec: 0, unit: "ppm" },
  { key: "phosphates",        label: "Phosphates",           min: 0,    max: 0.1,  dec: 1, unit: "ppm" },
  { key: "total_copper",      label: "Total Copper",         min: 0,    max: 0.5,  dec: 1, unit: "ppm" },
  { key: "visible_algae",     label: "Visible Algae",        min: 0,    max: 0.1,  dec: 1 },
];

// Tech-app tests are saved with short keys (phosphate in ppb): map them to the counter's names so both dose identically
const TECH_KEYS = { fc: "free_chlorine", tc: "total_chlorine", ta: "total_alkalinity", ch: "calcium_hardness", cya: "cyanuric_acid", phosphate: "phosphates", copper: "total_copper" };
export const canonReadings = (readings) => Object.fromEntries(Object.entries(readings || {}).map(([k, v]) =>
  [TECH_KEYS[k] || k, k === "phosphate" && Number.isFinite(Number(v)) ? Math.round(Number(v)) / 1000 : v]));

export const TEST_TYPES = ["Spin Disk", "Aqualux Nexus Pool Shop", "Manual"];
export const SPIN_DISKS = ["204", "304", "801"];

// Phone number kinds offered on the Add New Client popup
export const PHONE_TYPES = ["Mobile", "Work Phone", "Home Phone"];

export const WB_TYPES = ["Salt Pool", "Mineral Pool", "Chlorine Pool", "Magna Pool", "Naked Pool", "Salt Spa", "Chlorine Spa", "Bromine Swim Spa", "Chlorine Free Spa", "Salt Swim Spa", "Chlorine Swim Spa"];
export const SURFACES = ["Pebblecrete", "Glass Pebbles", "Marblesheen", "Quartzon", "Tiles", "Concrete", "Fibreglass", "Acrylic", "Vinyl"];
export const FILTERS = ["Sand Filter", "Glass Filter", "DE Filter", "Cartridge Filter"];
export const WATER_SOURCES = ["Tap Water", "Bore Water", "Tank Water", "Recycled Water"];
export const SALT_LEVELS = ["500 - 1000", "1000 - 1500", "1500 - 2000", "2000 - 2500", "2500 - 3000", "3000 - 3500", "3500 - 4000", "4000 - 4500", "4500 - 5000", "5000 - 5500", "5500 - 6000", "6000 - 6500", "6500 - 7000", "7000 - 8000", "8000 and above"];
export const MAG_LEVELS = ["600 - 800", "800 - 1000", "1000 - 1300", "1300 - 1600", "1600 - 2000"];
export const CHEM_BRANDS = ["Bioguard", "Iluka", "Lo-Chlor", "Spa Clear Ultra (Chlorine)", "Spa Fresh Ultra (Chlorine Free)", "Spa Solutions"];

export const CATEGORIES = [
  ["pool", "Pool"],
  ["spa", "Spa"],
  ["swim_spa_single", "Single Zone Swim Spa"],
  ["swim_spa_dual", "Dual Zone Swim Spa"],
  ["urban_pool_single", "Single Zone Urban Pool"],
  ["urban_pool_dual", "Dual Zone Urban Pool"],
];
// Legacy data stored older short codes — resolve their labels too
const CAT_ALIASES = { swim_spa: "Swim Spa", urban_pool: "Urban Pool" };
export const catLabel = (c) => ((CATEGORIES.find((x) => x[0] === c) || [])[1]) || CAT_ALIASES[c] || "Pool";

// Auto-select the LaMotte spin-tester disk from the body type + leafy flag.
// Mineral pool -> 801 (phosphate/mineral). Spa / Swim Spa / Urban Pool -> 304.
// Plain pool: leafy area -> 204 (phosphates), otherwise -> 304.
export function autoDisk(body) {
  const cat = (body?.category || "").toLowerCase();
  const type = (body?.type || "").toLowerCase();
  if (type.includes("mineral") || type.includes("magna")) return "801";
  const spaLike = cat !== "pool" && (cat.includes("spa") || cat.includes("swim") || cat.includes("urban") || type.includes("spa") || type.includes("swim"));
  if (spaLike) return "304";
  return body?.leafy ? "204" : "304";
}

// Map a water-body category code to the Chemical Range family (pool | spa | swimspa).
export function rangeCatFor(cat = "") {
  const c = (cat || "").toLowerCase();
  if (c.includes("swim") || c.includes("urban")) return "swimspa";
  if (c.includes("spa")) return "spa";
  return "pool";
}

// Flatten a Chemical Range's per-category matrix into a {role: primaryProductName} map for dosing.
export function flattenRange(range, cat) {
  if (!range) return null;
  const fam = rangeCatFor(cat);
  const src = (range.matrix && range.matrix[fam]) || {};
  const out = {};
  Object.keys(src).forEach((role) => {
    const first = (src[role] || [])[0];
    if (first && first.name) out[role] = first.name;
  });
  if (Object.keys(out).length === 0 && range.products) return { ...range.products };
  return out;
}

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// Which parameters apply to a body, inferred from its type/sanitiser (sensible defaults).
export function defaultEnabled(type) {
  const t = (type || "").toLowerCase();
  const salt = t.includes("salt") || t.includes("magna");
  const mineral = t.includes("mineral") || t.includes("magna") || t.includes("naked");
  const bromine = t.includes("bromine");
  const en = {};
  WT_PARAMS.forEach((p) => { en[p.key] = true; });
  en.salt = salt;
  en.magnesium = mineral;
  en.bromine = bromine;
  if (bromine) { en.free_chlorine = false; en.total_chlorine = false; en.combined_chlorine = false; en.cyanuric_acid = false; }
  return en;
}

// Water Test Setup type for a body: exact match first, else map old Spa Buddy sanitiser names
// ("Salt - 4000", "Smart Sticks", "Lithium Hypochlorite"…) onto the closest pool / spa / swim spa type.
export function templateType(body, templatesByType = {}) {
  const type = body?.type || "";
  if (templatesByType[type]) return type;
  const t = type.toLowerCase(), cat = String(body?.category || "pool").toLowerCase();
  const kind = /swim|urban/.test(cat) ? "Swim Spa" : cat.includes("spa") ? "Spa" : "Pool";
  if (kind === "Pool" && /mineral|magna/.test(t)) return "Mineral Pool";
  if (/salt/.test(t)) return `Salt ${kind}`;
  if (/bromine/.test(t)) return kind === "Pool" ? type : "Bromine Swim Spa";
  if (/peroxide|soft swim|ioni[sz]er|perfect balance|symmetry/.test(t)) return kind === "Spa" ? "Chlorine Free Spa" : type;
  return `Chlorine ${kind}`;
}

// Effective, ordered list of enabled params for a body.
// templatesByType: { [type]: { params: { key: {min,max,enabled} } } }
export function resolveParams(body, templatesByType = {}) {
  const type = body?.type || "";
  const tpl = templatesByType[templateType(body, templatesByType)];
  const en = defaultEnabled(type);
  const ov = (body && body.range_overrides) || {};
  const out = [];
  WT_PARAMS.forEach((p) => {
    let min = p.min, max = p.max, enabled = en[p.key];
    if (tpl && tpl.params && tpl.params[p.key]) {
      const tp = tpl.params[p.key];
      if (tp.min != null) min = tp.min;
      if (tp.max != null) max = tp.max;
      if (tp.enabled != null) enabled = tp.enabled;
    }
    const o = ov[p.key];
    if (o) {
      if (o.min != null && o.min !== "") min = parseFloat(o.min);
      if (o.max != null && o.max !== "") max = parseFloat(o.max);
      if (o.enabled != null) enabled = o.enabled;
    }
    if (enabled) out.push({ ...p, min, max });
  });
  return out;
}

// A row per master param with its resolved min/max + enabled flag (for the editors).
export function resolveAll(body, templatesByType = {}) {
  const type = body?.type || "";
  const tpl = templatesByType[templateType(body, templatesByType)];
  const en = defaultEnabled(type);
  const ov = (body && body.range_overrides) || {};
  return WT_PARAMS.map((p) => {
    let min = p.min, max = p.max, enabled = en[p.key];
    if (tpl && tpl.params && tpl.params[p.key]) {
      const tp = tpl.params[p.key];
      if (tp.min != null) min = tp.min;
      if (tp.max != null) max = tp.max;
      if (tp.enabled != null) enabled = tp.enabled;
    }
    const o = ov[p.key];
    if (o) {
      if (o.min != null && o.min !== "") min = parseFloat(o.min);
      if (o.max != null && o.max !== "") max = parseFloat(o.max);
      if (o.enabled != null) enabled = o.enabled;
    }
    return { ...p, min, max, enabled };
  });
}

export function evalValue(value, min, max) {
  const v = num(value);
  if (v === null) return null;
  return v >= min && v <= max ? "inside" : "outside";
}

// Backward-compatible: evaluate a reading against a param's default range (by key).
export function evalParam(key, value) {
  const p = WT_PARAMS.find((x) => x.key === key);
  if (!p) return null;
  return evalValue(value, p.min, p.max);
}

const TEMP_TABLE = [[0, 0.0], [3, 0.1], [8, 0.2], [12, 0.3], [16, 0.4], [19, 0.5], [24, 0.6], [29, 0.7], [34, 0.8], [41, 0.9], [53, 1.0]];
function tempFactor(t) {
  let f = 0.0;
  for (const [temp, fac] of TEMP_TABLE) { if (t >= temp) f = fac; }
  return f;
}

export function computeLSI(readings) {
  const ph = num(readings.ph);
  const temp = num(readings.temp);
  const ch = num(readings.calcium_hardness);
  const ta = num(readings.total_alkalinity);
  if (ph === null || temp === null || ch === null || ta === null || ch <= 0 || ta <= 0) return null;
  const lsi = ph + tempFactor(temp) + (Math.log10(ch) - 0.4) + Math.log10(ta) - 12.1;
  return Math.round(lsi * 100) / 100;
}

export function lsiState(lsi) {
  if (lsi === null) return "unknown";
  if (lsi < -0.3) return "corrosive";
  if (lsi > 0.3) return "scaling";
  return "balanced";
}

export function lsiPercent(lsi) {
  if (lsi === null) return 50;
  const clamped = Math.max(-1, Math.min(1, lsi));
  return ((clamped + 1) / 2) * 100;
}
